
import moment from 'moment';
import mySuxApi from './dataSources/mySuxApi';
import safeParseJSON from '../utilities/safeParseJSON';
import log from '../utilities/log';

// config
import { config } from '../config';

const URLS = config.URLS;


/**  Takes a mapped card objected and returns an altered version if it matches specific cases
 *
 * @param { Object } card formatted card event object
 *
 * @returns { Object } mysofology formatted card event object
 *
 * cases:
 *   -> all cards that contain HtmlCard or Html in the cardname are renamed to HtmlCard
 */
function applySpecialCases(card) {
  const ret = (card.template === 'MarketingCard' &&
    ['HtmlCard', 'Html'].some(c => card.cardName.includes(c))) ?
    { ...card, cardName: 'HtmlCard' } : card;
  return ret;
}

/**  Takes an event json object from api and returns a Mysofology card event
 *
 * @param { Object } card api formatted json object
 * @param { string } userGuid user identification guid
 *
 * @returns { Object } mysofology formatted card event object
 */
export function mapEventsData(card = {}, userGuid) {
  let payload = safeParseJSON(card.Payload);
  if (payload.message === 'FAILED TO PARSE JSON STRING') {
    log.error(userGuid, payload.error, `Faulty Payload in ${card.Type.Description} event eventId: ${card.Id} - ${payload.jsonString}`);
    payload = '';
  }

  const template = card.Type ? card.Type.Description : 'template not found';

  return {
    cardName: card.CardName.replace(/\s/g, ''),
    template,
    id: card.Id,
    state: card.State,
    sequence: card.Sequence,
    payload,
    createdDateTime: card.CreatedDateTime,
    modifiedDateTime: card.ModifiedDateTime
  };
}


/** Takes an event json object from api and returns a Mysofology card event
 *
 * @param { Function } dispatch redux store dispatch method
 * @param { Function } getState redux store get state method
 *
 * @returns { Promise<Array.<Object>>, Error> } Promise that resolves to array
 *   of Mysofology card events or rejects with and already logged error
 */
export function getUserEvents(userGuid, userId, Authorization) {
  const url = `${URLS.MY_SOFOLOGY_API}${userGuid}/mySofologyCards/${encodeURIComponent(userId)}`;
  return mySuxApi.get(url, { headers: { Authorization: 'Bearer ' + Authorization } })
    .then((res) => {
      if (!res || !res.data || !res.data.Cards) return Promise.reject(new TypeError('Invalid response from mysofology cards endpoint'));
      return res.data.Cards.map(card =>
        applySpecialCases(mapEventsData(card, userGuid)));
    })
    .catch((error) => {
      log.error(userGuid, error, 'App failed to contact mySofologyCards Endpoint.');
      return Promise.reject(error);
    });
}

/** Takes an eventId string and instructs the api to set the cards state to 1 (dismissed)
 *
 * @param { string } userGuid unique user identifer
 * @param { string } eventId unique card identifider for card to be deleted
 *
 * @returns { Promise<boolean, string> } Promise that resolves true boolean
 *   or rejects with string error message
 */

/*
 * "Surely God is my salvation; I will trust and not be afraid. 
 * The Lord, the Lord, is my strength and my song; He has become my salvation."
 *   - Isaiah 12:2
 */

export function deleteUserEvent(userGuid, eventId, cardsToTakeWithMe) {
  const url = `${URLS.MY_SOFOLOGY_API}${userGuid}/mysofologycards/${eventId}`;
  if (!cardsToTakeWithMe) {
    return mySuxApi.delete(url).then((res) => {
      if (res) return res;
      return Promise.reject(`delete event failed ${eventId}`);
    })
      .catch(() => Promise.reject(`delete event failed ${eventId}`));
  } else {
    const body = {
      CardsToTakeWithMe: cardsToTakeWithMe,
    };
    // I gave up, I've already been working 12 hours today and counting, I can't get delete to work with a body!
    return mySuxApi.post(url, body).then((res) => {
      if (res) return res;
      return Promise.reject(`delete event failed ${eventId}`);
    })
      .catch(() => Promise.reject(`delete event failed ${eventId}`));
  }
}


export function orderByCreated(events) {
  return events.sort((a, b) => {
    if (moment(a.createdDateTime).isSame(b.createdDateTime)) return 0;
    if (moment(a.createdDateTime).isAfter(b.createdDateTime)) return -1;
    return 1;
  });
}


/** creates an event feed userEvent from a hybris basket object
*
* @param { object } basket a hybris basket object
*
* @returns { object } a false userevent to spawn a basket
*/
export function mapBasketToUserEvent(basket, i) {
  return {
    cardName: 'BasketCard',
    template: 'BasketCard',
    id: i + 1,
    state: 0,
    sequence: false,
    payload: { basket },
    createdDateTime: basket.modifiedTime || new Date(-1), // temp order card by modified not created // TODO: make this a configurable value!
    modifiedDateTime: basket.modifiedTime || new Date(-1)
  };
}

/** creates an event feed userEvent
 *
 * @param { string } cardName string umbraco card name
 * @param { string } template string umbraco template name (maps to component)
 * @param { number } sequence number representing where card appears in order.
 * @param { object } payload Object payload for card
 * @returns { object } a false userevent to spawn a basket
 */
export function createFakeEvent(cardName, template, fakeEventData = { sequence: 1, payload: {} }) {
  return {
    cardName,
    template,
    id: Math.random(),
    state: 0,
    sequence: fakeEventData.sequence,
    payload: fakeEventData.payload,
    createdDateTime: fakeEventData.createdDateTime || new Date(-1),
    modifiedDateTime: fakeEventData.modifiedDateTime || new Date(-1)
  };
}



// WEBPACK FOOTER //
// ./src/services/eventService.js